import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  measurementSketchSchema,
  type CreateMeasurementSketchInput,
  type MeasurementSketch,
} from '@tailoring-crm/shared';
import { useApiClient } from '../api/api-context.js';
import type { ApiClient } from '../api/client.js';

/**
 * The drawings that go with measurements. A sketch is saved on its own — always as a NEW one —
 * and then hung on whatever it belongs to (a profile, a garment type's outline, one order item) by
 * that owner's own write. See `measurement-sketches.ts` in the shared package.
 */

const BASE = '/measurement-sketches';

export async function createMeasurementSketch(
  client: ApiClient,
  input: CreateMeasurementSketchInput,
): Promise<MeasurementSketch> {
  return await client.post(BASE, input, { schema: measurementSketchSchema });
}

export async function getMeasurementSketch(
  client: ApiClient,
  sketchId: string,
): Promise<MeasurementSketch> {
  return await client.get(`${BASE}/${sketchId}`, { schema: measurementSketchSchema });
}

/**
 * One drawing, scene and all. Cached forever: a sketch never changes (a redraw is a new id), so a
 * scene once fetched is the scene for good.
 */
export function useMeasurementSketch(sketchId: string | null): UseQueryResult<MeasurementSketch> {
  const client = useApiClient();
  return useQuery({
    queryKey: ['measurement-sketches', sketchId],
    queryFn: () => getMeasurementSketch(client, sketchId as string),
    enabled: sketchId !== null,
    staleTime: Infinity,
  });
}
